import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  ApiAsset,
  CloudFlareResponse,
  CreateAssetMetadataInput,
  FbAssetMetadata,
  JsAssetMetadata,
} from '../_interfaces/others.interface';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { serverTimestamp } from '@angular/fire/firestore';
import { convertTimeStampsFb_Js, getNewId } from '../_utils/utils';

@Injectable({
  providedIn: 'root',
})
export class CloudflareService {
  private apiBase = `${environment.webDomain}/api`;
  private baseUrl = environment.assetsBaseUrl;

  constructor(private http: HttpClient, private afs: AngularFirestore) {}

  // ========== Firestore Asset Metadata Methods ==========

  /**
   * Get all assets from Firestore (one-time fetch, no real-time listener)
   * Only returns assets that have metadata records
   */
  async fetchAllAssetMetadata(): Promise<JsAssetMetadata[]> {
    const snapshot = await this.afs
      .collection<FbAssetMetadata>('assets', (ref) =>
        ref.where('deletedAt', '==', null).orderBy('updatedAt', 'desc')
      )
      .get()
      .toPromise();

    if (!snapshot) return [];

    return snapshot.docs.map(
      (doc) => convertTimeStampsFb_Js(doc.data()) as JsAssetMetadata
    );
  }

  /**
   * Create asset metadata in Firestore after uploading to R2
   */
  async createAssetMetadata(
    r2Key: string,
    originalName: string,
    contentType: string,
    size: number,
    metadata: CreateAssetMetadataInput
  ): Promise<JsAssetMetadata> {
    const uid = this.getLocalUserId();
    if (!uid) throw new Error('User not authenticated');

    const id = getNewId();
    const now = new Date();
    const url = `${this.baseUrl}/${r2Key}`;

    const assetMetadata: any = {
      id,
      r2Key,
      displayName: metadata.displayName,
      description: metadata.description || '',
      tags: metadata.tags || [],
      altText: metadata.altText || '',
      originalName,
      contentType,
      size,
      url,
      uid,
      createdBy: uid,
      updatedBy: uid,
      createdAt: now,
      updatedAt: now,
      cloudUpdatedAt: serverTimestamp(),
      deletedAt: null,
    };

    await this.afs.collection('assets').doc(id).set(assetMetadata);

    return {
      ...assetMetadata,
      cloudUpdatedAt: now,
    } as JsAssetMetadata;
  }

  /**
   * Soft delete asset metadata from Firestore
   */
  async deleteAssetMetadata(id: string): Promise<void> {
    const uid = this.getLocalUserId();
    if (!uid) throw new Error('User not authenticated');

    await this.afs.collection('assets').doc(id).update({
      deletedAt: new Date(),
      updatedAt: new Date(),
      updatedBy: uid,
      cloudUpdatedAt: serverTimestamp(),
    });
  }

  /**
   * Update asset metadata when file is replaced
   */
  async updateAssetMetadataOnReplace(id: string): Promise<void> {
    const uid = this.getLocalUserId();
    if (!uid) throw new Error('User not authenticated');

    await this.afs.collection('assets').doc(id).update({
      updatedAt: new Date(),
      updatedBy: uid,
      cloudUpdatedAt: serverTimestamp(),
    });
  }

  /**
   * Update asset metadata (displayName, description, tags, altText)
   */
  async updateAssetMetadata(
    id: string,
    metadata: CreateAssetMetadataInput
  ): Promise<void> {
    const uid = this.getLocalUserId();
    if (!uid) throw new Error('User not authenticated');

    await this.afs.collection('assets').doc(id).update({
      displayName: metadata.displayName,
      description: metadata.description || '',
      tags: metadata.tags || [],
      altText: metadata.altText || '',
      updatedAt: new Date(),
      updatedBy: uid,
      cloudUpdatedAt: serverTimestamp(),
    });
  }

  private getLocalUserId(): string | null {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.uid || null;
  }

  // ========== R2 Storage Methods (existing) ==========

  /**
   * @deprecated Use getAllAssetMetadata() instead for assets with metadata
   */
  getAllAssets(): Observable<CloudFlareResponse> {
    return this.http.get<CloudFlareResponse>(this.apiBase, {
      params: { type: 'assets-list' },
    });
  }

  /**
   * Upload a single file to R2
   * Returns the R2 response with filename
   */
  uploadAsset(file: File): Observable<CloudFlareResponse> {
    const formData = new FormData();
    formData.append('files', file);

    return this.http.post<CloudFlareResponse>(`${this.apiBase}`, formData, {
      params: { type: 'assets-upload' },
    });
  }

  /**
   * Upload multiple files to R2
   */
  uploadAssets(files: File[]): Observable<CloudFlareResponse> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return this.http.post<CloudFlareResponse>(`${this.apiBase}`, formData, {
      params: { type: 'assets-upload' },
    });
  }

  updateAsset(filename: string, newFile: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', newFile);

    return this.http.post(`${this.apiBase}`, formData, {
      params: {
        type: 'assets-update',
        id: filename,
      },
    });
  }

  /**
   * Delete asset from R2 storage
   */
  deleteAssetFromR2(filename: string): Observable<any> {
    return this.http.post(
      `${this.apiBase}`,
      {},
      {
        params: {
          type: 'assets-delete',
          id: filename,
        },
      }
    );
  }

  /**
   * @deprecated Use deleteAssetFromR2() for R2 deletion
   */
  deleteAsset(filename: string): Observable<any> {
    return this.deleteAssetFromR2(filename);
  }
}
